// import ImageHTML from '../imageHTML/imageHTML';
import ImageHTML from '../imageHTML/imgHTML';
import { PATHURLDOMAIN } from '../../constantes/constantes';
import { dateFormatDDmesAnio } from '../../functions/functions';

/**
 * Componente de renderizado para lista de resultados de búsqueda con tracking avanzado
 * 
 * @description Renderiza una lista de tarjetas de resultados con funcionalidad completa
 * incluyendo imágenes, metadatos, indicadores multimedia y tracking de interacciones.
 * Implementa comunicación iframe para analytics y navegación contextual apropiada.
 * Optimizado para contenido rico con soporte para videos, categorías y fechas.
 * 
 * **Características principales:**
 * - Renderizado de lista con validación defensiva
 * - Tracking de clicks con postMessage para analytics
 * - Indicadores visuales para contenido multimedia
 * - Navegación iframe con target apropiado
 * - Metadatos ricos (fecha, categoría, título)
 * - Keys optimizadas para rendimiento de React
 * - Accesibilidad con ARIA labels
 * 
 * **Validación defensiva:**
 * - Maneja arrays vacíos o datos inválidos
 * - Fallbacks seguros para propiedades faltantes
 * - Estrategia de keys robusta con múltiples fallbacks
 * 
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {Array} [props.data=[]] - Array de objetos de resultados de búsqueda
 * @param {string} props.data[].id - Identificador único del resultado (preferido para keys)
 * @param {string} props.data[].title - Título del artículo o contenido
 * @param {string} props.data[].url - URL de destino del resultado
 * @param {string} props.data[].image - URL de la imagen de preview
 * @param {string} props.data[].category - Categoría principal del contenido
 * @param {string} [props.data[].subcategory] - Subcategoría (prioridad sobre category)
 * @param {string} props.data[].date - Fecha de publicación (formato ISO)
 * @param {Array<string>} [props.data[].tags] - Tags del contenido (ej: ["video", "destacado"])
 * 
 * @returns {JSX.Element|null} Lista de tarjetas de resultados o null si no hay datos
 * 
 * @example
 * // Uso básico con resultados de búsqueda
 * const searchResults = [
 *   {
 *     id: "123",
 *     title: "Noticia importante",
 *     url: "https://example.com/noticia",
 *     image: "https://example.com/image.jpg",
 *     category: "deportes",
 *     subcategory: "futbol",
 *     date: "2024-01-15T10:30:00Z",
 *     tags: ["video", "destacado"]
 *   }
 * ];
 * 
 * <CardResults data={searchResults} />
 * 
 * @example
 * // Integración con componente Results
 * const Results = ({ data, query }) => {
 *   const dataQ = data && Array.isArray(data.hits) ? data.hits : [];
 *   
 *   return dataQ.length === 0
 *     ? <WithoutResults query={query} />
 *     : <CardResults data={dataQ} />;
 * };
 * 
 * @example
 * // Casos de validación defensiva manejados:
 * <CardResults data={undefined} />        // → null (no renderiza)
 * <CardResults data={null} />             // → null (no renderiza)
 * <CardResults data={[]} />               // → null (array vacío)
 * <CardResults data={"invalid"} />        // → null (no es array)
 * <CardResults data={[validItem]} />      // → Renderiza lista
 * 
 * @example
 * // Estrategia de keys optimizada:
 * // Prioridad: item.id > item.url > índice
 * key={item.id || item.url || `item-${id}`}
 * // Garantiza keys únicas y estables para React
 * 
 * @example
 * // Indicadores multimedia automáticos:
 * // Si item.tags incluye "video":
 * <span className="amx-icon-play" aria-label="Video"></span>
 * // Proporciona feedback visual y accesibilidad
 * 
 * @example
 * // Formateo de categorías:
 * const category = item.subcategory || item.category || 'Sin categoría';
 * const displayCategory = category.replace(/-/g, ' ');
 * // Convierte "futbol-internacional" → "futbol internacional"
 * 
 * @since 1.0.0
 * @author Valeria Garcia
 * 
 * @see {@link ../imageHTML/imgHTML.js} Componente para renderizado de imágenes
 * @see {@link ./results.js} Componente padre que utiliza CardResults
 * @see {@link ../../functions/functions.js#dateFormatDDmesAnio} Función de formateo de fechas
 * @see {@link ../../constantes/constantes.js#PATHURLDOMAIN} Dominio para postMessage
 */
export default function CardResults({ data = [] }) {
  if (!Array.isArray(data) || data.length === 0) {
    return null; // o <div>No hay resultados</div>
  }

  /**
   * Envía un mensaje al padre con un tipo específico de evento.
   * Utilizado para el seguimiento de interacciones (tageo).
  */
  const sendMessageToParent = (data) => {
    window.parent.postMessage({ type: 'tageo', data }, PATHURLDOMAIN);
  };

  // Manejador del clic sobre el resultado.
  const handleButtonClick = (e, item) => {
    // e.preventDefault();
    sendMessageToParent({ event: 'click', data: item });
  };

  return (
    <div id="resultados" role="region" aria-label="Resultados de búsqueda">
      {data.map((item, id) => (
        <div key={item.id || item.url || `item-${id}`} className="amx-notas-homes">
          <a className="item" href={item.url} title={item.title} target="_parent" onClick={(e) => handleButtonClick(e, item)} aria-label={`${item.title} - Publicado el ${dateFormatDDmesAnio(item.date)}`}>
            <div className="thumb">
              {item.tags && item.tags.includes("video") && <span className="amx-icon-play" aria-label="Video"></span>}
              <ImageHTML url={item.image} title={item.title} />
            </div>

            <div className="info">
              <h4>{(item.subcategory ? item.subcategory : item.category).replace(/-/g, ' ')}</h4>
              <h3>{item.title}</h3>
              <time dateTime={item.date} className="datetime" aria-label={`Publicado el ${dateFormatDDmesAnio(item.date)}`}>{dateFormatDDmesAnio(item.date)}</time>
            </div>
          </a>
        </div>
      ))}
    </div>
  );
}